import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Api from '../Network';
import { message } from 'antd';
import { getErrorMessage } from '../utils/getErrorMessage';

export interface ListFilters {
  filters: {
    search?: string;
    isDeleted?: boolean;
    [key: string]: any;
  };
  paging: {
    page: number;
    pageSize: number;
  };
  sorting: {
    [key: string]: 1 | -1;
  };
}

type QueryOptions = {
  useQueryOptions?: any;
  baseURL?: string;
  initialFilters?: ListFilters;
  disableErrorHandling?: boolean;
  method?: 'GET' | 'POST';
};

export function useList(key?: string, useListOptions?: QueryOptions) {
  const [filters, setFilters] = useState<ListFilters>(
    useListOptions?.initialFilters || {
      filters: { search: '' },
      paging: { page: 1, pageSize: 20 },
      sorting: {},
    }
  );

  function setListFilters(path: string, value: any) {
    setFilters((filters) => {
      const newFilters = { ...filters } as any;
      const pathParts = path.split('.');
      let current = newFilters;

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return newFilters;
    });
  }

  useEffect(() => {
    setListFilters('paging', useListOptions?.initialFilters?.paging || { page: 1, pageSize: 20 });
  }, []); // Only run once on mount

  const queryResult = useQuery({
    queryKey: [key, filters],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const method = useListOptions?.method || 'GET';

      if (method === 'POST') {
        // Use POST method with filters in body
        return Api.post(key || '', filters, {
          signal,
          baseURL: useListOptions?.baseURL,
        });
      } else {
        // Build query parameters for GET request
        const params = new URLSearchParams();

        // Add pagination
        params.append('page', filters.paging.page.toString());
        params.append('per-page', filters.paging.pageSize.toString()); // Will be converted to header by Network interceptor

        // Add all filters in Laravel QueryBuilder format
        Object.entries(filters.filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            // For products API, search should be sent as filter[name]
            if (key === 'search' && value) {
              params.append('filter[name]', value.toString());
            } else {
              params.append(`filter[${key}]`, value.toString());
            }
          }
        });

        // Add deleted filter (special case)
        if (filters.filters.isDeleted) {
          params.append('deleted', '1');
        }

        // Add sorting
        if (Object.keys(filters.sorting).length > 0) {
          const [field, direction] = Object.entries(filters.sorting)[0];
          params.append('sort', field);
          params.append('order', direction === 1 ? 'asc' : 'desc');
        }

        const url = `${key}?${params.toString()}`;
        return Api.get(url, {
          signal,
          baseURL: useListOptions?.baseURL,
        });
      }
    },
    enabled: !!key,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    ...useListOptions?.useQueryOptions,
  });

  useEffect(() => {
    if (queryResult.error && !useListOptions?.disableErrorHandling) {
      message.error(getErrorMessage(queryResult.error, `Failed to load ${key}`));
    }
  }, [queryResult.error, useListOptions?.disableErrorHandling, key]);

  return {
    filters,
    setListFilters,
    ...queryResult,
  };
}
